import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { core } from '@/api/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import styles from './AdminAnnouncementsPage.module.css';

interface Announcement {
  id: string;
  title: string;
  target: string;
  status: 'PUBLISHED' | 'DRAFT';
  publishedAt?: string;
  message?: string;
  campusId?: string;
}

const TARGETS = ['ALL', 'STUDENTS', 'PARENTS', 'TEACHERS', 'STAFF'];
const TARGET_I18N_MAP: Record<string, string> = {
  ALL: 'admin.announcements.targetAll',
  STUDENTS: 'admin.announcements.targetStudents',
  PARENTS: 'admin.announcements.targetParents',
  TEACHERS: 'admin.announcements.targetTeachers',
  STAFF: 'admin.announcements.targetStaff',
};

function formatDate(ts?: string): string {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return ts;
  }
}

export function AdminAnnouncementsPage() {
  const { t } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const { state, reload } = useAsyncData<Announcement[]>(
    `admin-announcements-${institutionId}`,
    () => core<Announcement[]>(`/admin/institution/${institutionId}/announcements`).catch(() => []),
    !!institutionId,
  );

  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', message: '', target: 'ALL', campusId: '', asDraft: true });
  const [saving, setSaving] = useState(false);

  const handleCreate = useCallback(async () => {
    if (!newAnnouncement.title.trim()) return;
    setSaving(true);
    try {
      await core(`/admin/institution/${institutionId}/announcements`, {
        method: 'POST',
        body: JSON.stringify({
          title: newAnnouncement.title,
          message: newAnnouncement.message,
          target: newAnnouncement.target,
          campusId: newAnnouncement.campusId || undefined,
          status: newAnnouncement.asDraft ? 'DRAFT' : 'PUBLISHED',
        }),
      });
      setModalOpen(false);
      setNewAnnouncement({ title: '', message: '', target: 'ALL', campusId: '', asDraft: true });
      toast.success(t('admin.announcements.createSuccess'));
      reload();
    } catch {
      // The modal stays open so the text survives for a retry.
      toast.error(t('admin.announcements.createFailed'));
    } finally {
      setSaving(false);
    }
  }, [institutionId, newAnnouncement, reload, t, toast]);

  if (state.status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  if (state.status === 'error') {
    const error = state.error;
    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name={error.isMissingEndpoint ? 'info' : 'error'} size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.announcements.title')}</h2>
          <p className={styles.emptyStateBody}>
            {t(error.isMissingEndpoint ? 'platform.failure.missing.body' : 'platform.failure.generic.body')}
          </p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      </div>
    );
  }

  const announcements = state.data || [];

  return (
    <div className={styles.page}>
      <div className="page-header page-headerRow">
        <div>
          <h1>{t('admin.announcements.title')}</h1>
          <p className="page-subtitle">{t('admin.announcements.subtitle')}</p>
        </div>
        <div className={styles.headerActions}>
          <Button onClick={() => setModalOpen(true)}>
            <Icon name="plus" size={16} />
            {t('admin.announcements.create')}
          </Button>
        </div>
      </div>

      <Card>
        <div className="table-scroll">
          <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('admin.announcements.titleLabel')}</th>
              <th>{t('admin.announcements.target')}</th>
              <th>{t('admin.announcements.publishedAt')}</th>
              <th>{t('admin.announcements.status')}</th>
            </tr>
          </thead>
          <tbody>
            {announcements.length === 0 ? (
              <tr className={styles.noDataRow}>
                <td colSpan={4}>{t('admin.announcements.noAnnouncements')}</td>
              </tr>
            ) : (
              announcements.map((a) => (
                <tr key={a.id}>
                  <td className={styles.cellTitle}>{a.title}</td>
                  <td>{t(TARGET_I18N_MAP[a.target] || `admin.announcements.targetAll`, a.target)}</td>
                  <td>{formatDate(a.publishedAt)}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${a.status === 'PUBLISHED' ? styles.statusPublished : styles.statusDraft}`}>
                      {t(a.status === 'PUBLISHED' ? 'admin.announcements.publish' : 'admin.announcements.draft')}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          </table>
        </div>
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('admin.announcements.create')}
        closeOnBackdropClick={false}
      >
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="ann-title">{t('admin.announcements.titleLabel')}</label>
          <input
            id="ann-title"
            className={styles.fieldInput}
            type="text"
            placeholder={t('admin.announcements.titlePlaceholder')}
            value={newAnnouncement.title}
            onChange={(e) => setNewAnnouncement((p) => ({ ...p, title: e.target.value }))}
          />
        </div>
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="ann-message">{t('admin.announcements.messageBody')}</label>
          <textarea
            id="ann-message"
            className={styles.fieldTextarea}
            placeholder={t('admin.announcements.messagePlaceholder')}
            value={newAnnouncement.message}
            onChange={(e) => setNewAnnouncement((p) => ({ ...p, message: e.target.value }))}
          />
        </div>
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="ann-target">{t('admin.announcements.target')}</label>
          <Select
            id="ann-target"
            value={newAnnouncement.target}
            onChange={(value) => setNewAnnouncement((p) => ({ ...p, target: value }))}
            options={TARGETS.map((tg) => ({
              value: tg,
              label: t(TARGET_I18N_MAP[tg] || `admin.announcements.targetAll`, tg),
            }))}
          />
        </div>
        <div className={styles.toggleRow}>
          <span className={styles.toggleLabel}>{t('admin.announcements.publish')}</span>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={!newAnnouncement.asDraft}
              onChange={(e) => setNewAnnouncement((p) => ({ ...p, asDraft: !e.target.checked }))}
            />
            <span className={styles.toggleSlider} />
          </label>
        </div>
        <div className={styles.modalActions}>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleCreate} loading={saving} loadingLabel={t('common.loading')} disabled={!newAnnouncement.title.trim()}>
            {newAnnouncement.asDraft ? t('admin.announcements.saveAsDraft') : t('admin.announcements.publish')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
